import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAdminBlogBySlug, deleteAdminBlog } from '../../../store/slices/blogAdminSlice';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import toast from 'react-hot-toast';

const AdminBlogDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // URL parameter acts as the slug or ID
    const dispatch = useAppDispatch();

    const { currentBlog, loading } = useAppSelector((state) => state.blogAdmin);
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchAdminBlogBySlug(id));
        }
    }, [id, dispatch]);

    const handleEdit = () => {
        navigate(`/admin/blogs/${id}/edit`);
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            // Need the actual numeric ID for deletion if backend requires it, 
            // but the blogAdminSlice handles deleteAdminBlog(id). 
             await dispatch(deleteAdminBlog(currentBlog?.id || id)).unwrap();
             toast.success('Blog deleted successfully');
             navigate('/admin/blogs');
        } catch (error) {
             const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to delete blog');
             toast.error(errorMessage);
        } finally {
             setIsDeleting(false);
             setShowDeleteModal(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown Date';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    if (loading || !currentBlog) {
        return (
            <div className="flex justify-center items-center h-full min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-[#ecf0f5] min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Blog Details</h1>
                <button
                    onClick={() => navigate('/admin/blogs')}
                    className="flex items-center gap-2 bg-[#ffd200] hover:bg-[#e6bd00] text-black px-6 py-2 rounded shadow-sm font-bold transition-all"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back</span>
                </button>
            </div>

            {/* Content Container */}
            <div className="bg-white rounded shadow-sm overflow-hidden relative">
                {/* Top Action Buttons (Edit/Delete) mapped from screenshot */}
                <div className="absolute top-6 right-6 flex gap-2">
                    <button
                        onClick={handleEdit}
                        className="w-10 h-10 flex items-center justify-center bg-[#ffd200] text-black rounded hover:bg-[#e6bd00] transition-colors shadow-sm"
                        title="Edit Blog"
                    >
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button
                        onClick={handleDeleteClick}
                        className="w-10 h-10 flex items-center justify-center bg-[#e74c3c] text-white rounded hover:bg-[#c0392b] transition-colors shadow-sm"
                        title="Delete Blog"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>

                {/* Title & Meta Info */}
                <div className="p-8 pb-6 border-b border-gray-100">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3 pr-24 leading-snug">
                        {currentBlog.title}
                    </h2>
                    <p className="text-gray-900 font-bold text-sm">
                        Author: {currentBlog.created_by || currentBlog.author || ''} {currentBlog.created_at ? `on ${formatDate(currentBlog.created_at)}` : ''}
                    </p>
                </div>

                {/* Large Hero Image */}
                <div className="w-full bg-gray-50 flex justify-center">
                    {currentBlog.image_url ? (
                        <img 
                            src={`${currentBlog.image_url}`}
                            alt={currentBlog.title}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/hero-image.jpeg';
                            }}
                            className="w-full max-h-[600px] object-cover"
                        />
                    ) : (
                        <div className="py-20 text-gray-400 italic">No image available</div>
                    )}
                </div>

                {/* Main Content Rendered */}
                <div className="p-8 text-gray-700 leading-relax max-w-none prose prose-lg">
                    {currentBlog.page_content ? (
                        <div dangerouslySetInnerHTML={{ __html: currentBlog.page_content }} />
                    ) : (
                        <p className="italic text-gray-500">No content available.</p>
                    )}
                </div>
            </div>

            <UniversalConfirmModal
                isOpen={showDeleteModal}
                onClose={() => !isDeleting && setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                type="delete"
                title="Are you sure?"
                message="You are about to DELETE this Blog. Click DELETE to proceed or CANCEL to exit."
                confirmText={isDeleting ? 'DELETING...' : 'DELETE'}
                cancelText="CANCEL"
                theme="modern"
            />
        </div>
    );
};

export default AdminBlogDetail;
